import type { LogicNode, EvalContext } from '../types';
import { tokenize } from './tokenizer';
import { parse } from './parser';

const MAX_DEPTH = 15;

/** Parse and cache expressions to avoid re-parsing. */
const parseCache = new Map<string, LogicNode>();

export function parseExpression(rule: string): LogicNode {
  const cached = parseCache.get(rule);
  if (cached) return cached;
  const tokens = tokenize(rule);
  const node = parse(tokens);
  parseCache.set(rule, node);
  return node;
}

/**
 * Evaluate a LogicNode against the given context.
 * Returns true if the requirement is satisfied.
 */
export function evaluate(node: LogicNode, ctx: EvalContext, depth = 0): boolean {
  if (depth > MAX_DEPTH) return true; // prevent infinite recursion

  switch (node.type) {
    case 'literal':
      return node.value;

    case 'and':
      return evaluate(node.left, ctx, depth) && evaluate(node.right, ctx, depth);

    case 'or':
      return evaluate(node.left, ctx, depth) || evaluate(node.right, ctx, depth);

    case 'not':
      return !evaluate(node.child, ctx, depth);

    case 'has':
      return evaluateItem(node.item, node.count, ctx, depth);

    case 'call':
      return evaluateCall(node.name, node.args, ctx, depth);

    case 'compare':
      return evaluateCompare(node.left, node.op, node.right, ctx);

    case 'event':
      // Events are assumed to be completable (v1 simplification)
      return true;

    case 'contains':
      // 'X' in setting_name -> check if setting contains X
      return evaluateContains(node.left, node.right, ctx);
  }
}

function evaluateItem(item: string, count: number, ctx: EvalContext, depth: number): boolean {
  // Check if it's a helper alias first
  if (item in ctx.helpers) {
    const expanded = parseExpression(ctx.helpers[item]);
    if (count > 1) {
      // For (Helper, N), check if item count >= N after resolving the base item
      // Try to resolve the helper to a base item name
      return (ctx.items[item] ?? 0) >= count || evaluate(expanded, ctx, depth + 1);
    }
    return evaluate(expanded, ctx, depth + 1);
  }

  // Check age-related identifiers
  if (item === 'age') return true; // handled via compare
  if (item === 'starting_age') return true;

  // Trick identifiers -> false (we ignore tricks)
  if (item.startsWith('logic_')) return false;

  // Settings that are boolean
  if (item in ctx.settings) {
    return !!ctx.settings[item];
  }

  // Item inventory check
  return (ctx.items[item] ?? 0) >= count;
}

function evaluateCall(name: string, args: string[], ctx: EvalContext, depth: number): boolean {
  // has_soul -> always true (no soul tracking)
  if (name === 'has_soul') return true;

  // at() and here() -> true (v1 simplification)
  if (name === 'at' || name === 'here') {
    // Try to evaluate the condition arg if present
    if (args.length >= 1) {
      // For here(condition), evaluate the condition
      const condArg = args[args.length - 1];
      try {
        const condNode = parseExpression(condArg);
        return evaluate(condNode, ctx, depth + 1);
      } catch {
        return true;
      }
    }
    return true;
  }

  // can_pass_boulder -> true (simplification)
  if (name === 'can_pass_boulder') return true;

  // has_all_notes_for_song -> true (simplification)
  if (name === 'has_all_notes_for_song') return true;

  // _is_magic_item, _is_adult_item, _is_child_item -> used by can_use helper
  if (name === '_is_magic_item') {
    const MAGIC_ITEMS = ['Dins_Fire', 'Farores_Wind', 'Nayrus_Love', 'Lens_of_Truth'];
    return MAGIC_ITEMS.includes(args[0]);
  }
  if (name === '_is_adult_item') {
    const ADULT_ITEMS = [
      'Bow', 'Megaton_Hammer', 'Iron_Boots', 'Hover_Boots',
      'Hookshot', 'Longshot', 'Silver_Gauntlets', 'Golden_Gauntlets',
      'Goron_Tunic', 'Zora_Tunic', 'Mirror_Shield',
      'Fire_Arrows', 'Ice_Arrows', 'Light_Arrows', 'Blue_Fire_Arrows',
    ];
    return ADULT_ITEMS.includes(args[0]);
  }
  if (name === '_is_child_item') {
    const CHILD_ITEMS = ['Slingshot', 'Boomerang', 'Kokiri_Sword', 'Sticks', 'Deku_Shield'];
    return CHILD_ITEMS.includes(args[0]);
  }

  // Try to expand as a helper with parameter substitution
  // Helpers like can_use(item), can_play(song) have templates
  const templateKey = `${name}(${args.map((_, i) => `arg${i}`).join(', ')})`;
  // Look for helper with function-style key
  for (const [key, value] of Object.entries(ctx.helpers)) {
    const match = key.match(/^(\w+)\(([^)]*)\)$/);
    if (match && match[1] === name) {
      const params = match[2].split(',').map(p => p.trim());
      let expanded = value;
      params.forEach((param, i) => {
        if (i < args.length) {
          // Replace all occurrences of the parameter with the actual argument
          expanded = expanded.replace(new RegExp(`\\b${param}\\b`, 'g'), args[i]);
        }
      });
      try {
        const node = parseExpression(expanded);
        return evaluate(node, ctx, depth + 1);
      } catch {
        return true;
      }
    }
  }

  // If no helper found, check if it's a simple helper name
  if (name in ctx.helpers) {
    const node = parseExpression(ctx.helpers[name]);
    return evaluate(node, ctx, depth + 1);
  }

  // Unknown function -> true
  return true;
}

function evaluateCompare(left: string, op: '==' | '!=', right: string, ctx: EvalContext): boolean {
  // Special: age comparison
  if (left === 'age') {
    const result = ctx.age === right;
    return op === '==' ? result : !result;
  }

  // Setting comparison
  const settingVal = ctx.settings[left];
  if (settingVal !== undefined) {
    const strVal = String(settingVal);
    const result = strVal === right;
    return op === '==' ? result : !result;
  }

  // Unknown setting -> assume matches for ==
  return op === '==';
}

function evaluateContains(value: string, settingName: string, ctx: EvalContext): boolean {
  const setting = ctx.settings[settingName];
  if (Array.isArray(setting)) {
    return setting.includes(value);
  }
  if (typeof setting === 'string') {
    return setting.includes(value);
  }
  return false;
}

/**
 * Evaluate a rule string for both ages.
 * Returns { child: boolean, adult: boolean }.
 */
export function evaluateBothAges(
  rule: string,
  items: Record<string, number>,
  settings: Record<string, unknown>,
  helpers: Record<string, string>,
): { child: boolean; adult: boolean } {
  const node = parseExpression(rule);

  const childCtx: EvalContext = { age: 'child', items, settings, helpers };
  const adultCtx: EvalContext = { age: 'adult', items, settings, helpers };

  return {
    child: evaluate(node, childCtx),
    adult: evaluate(node, adultCtx),
  };
}
